// Recoil 전역 상태 모음 — 페이지 간 공유되는 것만 둔다. 검색 결과·상세검색 상태는 훅(useLectureSearch/useDetailedSearch)이 소유.
// 목록: searchFiltersState(태그 필터) · uiState · engModeAtom(ENG 모드 — localStorage 'starinfo-eng-mode', 없으면 브라우저 언어로 추정)
//   · isAdminAtom(sessionStorage 토큰 유무, 탭 간 동기화) · tagLabelsMapAtom(영문 라벨) · hideSidebarAtom
//   · thumbnailOpacityAtom / thumbnailAltModeAtom / thumbnailShuffleNonceAtom(썸네일 표시 모드) · ladderGradeModeAtom(등급 배지 표시).
import { atom } from 'recoil';
import posthog from 'posthog-js';
import { SearchFilters } from '../types/types';
import { getAdminToken, setAdminToken, requestAdminTokenFromOtherTabs } from '../features/admin/api/adminApi';

// 검색 필터 상태
export const searchFiltersState = atom<SearchFilters>({
    key: 'searchFiltersState',
    default: {
        selectedTags: [],
        selectedChannels: [],
        matchup: undefined,
    },
});



// UI 상태 관리
export const uiState = atom({
    key: 'uiState',
    default: {
        showFilters: true,
        isMobile: false,
        darkMode: true,
    },
});

// ENG 모드 — 2026-09-30 일반 공개. 초기값 우선순위:
//   1) URL ?eng=1 / ?eng=0 (이력서 등 외부 딥링크)
//   2) 사용자가 토글로 직접 고른 값 (localStorage — 자동 감지로 켜진 걸 끈 사람이 매 방문마다
//      다시 영어를 보지 않도록)
//   3) 브라우저 언어가 한국어가 아니면 on (크롤러 제외 — Googlebot은 en-US로 렌더링하므로 그대로
//      두면 한국어 SEO 키워드가 영어 UI로 색인될 수 있음)
const ENG_PREF_KEY = 'starinfo-eng-mode';
const LEGACY_ENG_PREF_KEY = 'bwbg-eng-mode'; // 2026-10-20 이전 키 — 한 번 읽어 새 키로 옮기고 지운다
const BOT_UA_RE = /bot|crawl|spider|slurp|yeti|daum|headless|lighthouse|prerender/i;

function readEngPref(): boolean | null {
    try {
        let v = localStorage.getItem(ENG_PREF_KEY);
        if (v === null) {
            v = localStorage.getItem(LEGACY_ENG_PREF_KEY);
            if (v !== null) {
                localStorage.setItem(ENG_PREF_KEY, v);
                localStorage.removeItem(LEGACY_ENG_PREF_KEY);
            }
        }
        return v === '1' ? true : v === '0' ? false : null;
    } catch {
        return null;
    }
}

function detectNonKoreanBrowser(): boolean {
    if (BOT_UA_RE.test(navigator.userAgent) || navigator.webdriver) return false;
    const lang = (navigator.languages?.[0] ?? navigator.language ?? '').toLowerCase();
    return lang !== '' && !lang.startsWith('ko');
}

function initialEngMode(): boolean {
    const param = new URLSearchParams(window.location.search).get('eng');
    if (param === '1') return true;
    if (param === '0') return false;
    // /en/lecture/:id 등 영문 경로로 들어오면 저장된 선호보다 경로를 우선(2026-10-01) — 구글
    // hreflang="en" 결과나 ENG 모드 사용자가 공유한 링크로 온 사람에게 한국어 UI를 보여주지 않도록.
    // ?eng=1과 마찬가지로 localStorage엔 저장하지 않는다.
    if (window.location.pathname.startsWith('/en/')) return true;
    return readEngPref() ?? detectNonKoreanBrowser();
}

export const engModeAtom = atom<boolean>({
    key: 'engModeAtom',
    default: false,
    effects: [
        ({ setSelf, onSet }) => {
            const initial = initialEngMode();
            if (initial) setSelf(true);
            // 모든 PostHog 이벤트에 eng_mode를 붙여 영어로 보는 사용자의 행동을 따로 볼 수 있게(2026-10-24)
            try { posthog.register({ eng_mode: initial }); } catch {}
            // 토글로 직접 바꾼 값만 기억 (위 setSelf 같은 effect 내부 변경은 onSet에 안 잡힘)
            onSet((newValue) => {
                try {
                    posthog.register({ eng_mode: newValue });
                    posthog.capture('eng_mode_toggled', { enabled: newValue, path: window.location.pathname });
                    (window as any).gtag?.('event', 'eng_mode_toggled', { enabled: newValue });
                } catch {}
                try {
                    localStorage.setItem(ENG_PREF_KEY, newValue ? '1' : '0');
                } catch {
                    // 저장 불가 환경(시크릿 모드 등) — 이번 세션에만 적용
                }
            });
        },
        // 탭 간 동기화 (BroadcastChannel) — adminApi.ts의 인증 동기화와 동일 패턴(2026-09-27).
        // 민감한 값이 아니라 요청-응답 왕복 없이 단순 브로드캐스트로 충분: 어느 탭이든 켜지면
        // 그 순간 열려있는 다른 탭에도 알리고(끄는 것도 동일), 새로 여는 탭은 열리자마자 "누가 켜져있으면
        // 알려줘"라고 물어본다. ?eng=1 딥링크는 localStorage에 저장되지 않으므로 이 채널이 있어야
        // 딥링크 탭에서 새 탭으로 연 상세페이지도 영어로 이어진다.
        ({ setSelf, onSet }) => {
            // 위 effect가 이미 setSelf(true)를 호출했을 수 있는데, 그건 이 effect의 onSet
            // 구독보다 먼저 일어나는 초기화라 onSet에 안 잡힌다 — isOn을 false로 시작하면 정작
            // "이력서 링크로 막 들어온, eng 켜진 탭"이 다른 새 탭의 request-state에 응답을 못 한다
            // (2026-09-27 발견). 초기값을 직접 다시 계산해 이 문제를 피한다.
            let isOn = initialEngMode();
            let channel: BroadcastChannel | null = null;
            try {
                channel = new BroadcastChannel('starinfo-eng-mode-sync');
            } catch {
                channel = null; // 미지원 브라우저 — 조용히 생략
            }
            if (!channel) return;

            channel.onmessage = (e: MessageEvent) => {
                if (e.data?.type === 'eng-on') {
                    isOn = true;
                    setSelf(true);
                } else if (e.data?.type === 'eng-off') {
                    isOn = false;
                    setSelf(false);
                } else if (e.data?.type === 'request-state' && isOn) {
                    channel!.postMessage({ type: 'eng-on' });
                }
            };
            channel.postMessage({ type: 'request-state' });

            onSet((newValue) => {
                isOn = newValue;
                channel!.postMessage({ type: newValue ? 'eng-on' : 'eng-off' });
            });

            return () => channel!.close();
        },
    ],
});

// 현재 탭이 어드민 인증 상태인지 (상세페이지 EN/수정마크, 카드 어드민 표시 등에서 공용으로 사용).
// 이 탭의 sessionStorage에 토큰이 없으면 같은 브라우저의 다른 로그인된 탭에게 즉시 물어본다
// (2026-09-16 — 새 탭으로 상세페이지에 바로 들어가면 /admin을 한 번 거치기 전까진 관리자 표시가
// 하나도 안 뜬다는 신고로 추가; 기존엔 각 컴포넌트가 !!getAdminToken()을 마운트 시점에 한 번만
// 읽어서 이 비동기 동기화 결과를 반영할 방법이 없었음). localStorage는 여전히 안 씀.
export const isAdminAtom = atom<boolean>({
    key: 'isAdminAtom',
    default: false,
    effects: [
        ({ setSelf }) => {
            if (getAdminToken()) {
                setSelf(true);
                return;
            }
            requestAdminTokenFromOtherTabs().then(token => {
                if (token) {
                    setAdminToken(token);
                    setSelf(true);
                }
            });
        },
    ],
});

// canonical_name → en 매핑 테이블
export const tagLabelsMapAtom = atom<Record<string, string>>({
    key: 'tagLabelsMapAtom',
    default: {},
});

// 태그 사이드바 숨기기 (업무/수업 모드)
export const hideSidebarAtom = atom<boolean>({
    key: 'hideSidebarAtom',
    default: false,
    effects: [
        ({ setSelf, onSet }) => {
            const saved = localStorage.getItem('hideSidebar');
            if (saved !== null) setSelf(saved === 'true');
            onSet((val) => localStorage.setItem('hideSidebar', String(val)));
        },
    ],
});

// 검색 결과 페이지네이션 방식(2026-10-22, 어드민 전용 A/B/C 테스트 — 사용자: "스크롤 너무 길어짐, 몇 페이지로 넘어갈지 옵션").
// more = 지금의 20개씩 누적 "더보기"(공개 기본) · pages = 페이지 단위 교체 + 번호 · pages-jump = pages + 위에도 컨트롤 + 번호 직접 입력.
// 2026-10-22 사용자 결정으로 공개 사용자는 항상 pages-jump(C) — SearchPage가 어드민일 때만 이 atom을 읽는다. 어드민 선택은 localStorage에 기억
export type PaginationMode = 'more' | 'pages' | 'pages-jump';
export const paginationModeAtom = atom<PaginationMode>({
    key: 'paginationModeAtom',
    default: 'pages-jump',
    effects: [
        ({ setSelf, onSet }) => {
            try {
                const saved = localStorage.getItem('starinfo-pagination-mode');
                if (saved === 'more' || saved === 'pages' || saved === 'pages-jump') setSelf(saved);
            } catch {}
            onSet((val) => {
                try { localStorage.setItem('starinfo-pagination-mode', val); } catch {}
            });
        },
    ],
});

// 썸네일 투명도 (0=스켈레톤, 1~99=반투명, 100=원래대로)
export const thumbnailOpacityAtom = atom<number>({
    key: 'thumbnailOpacityAtom',
    default: 100,
    effects: [
        ({ setSelf, onSet }) => {
            const saved = localStorage.getItem('thumbnailOpacity');
            if (saved !== null) {
                const parsed = Number(saved);
                if (!isNaN(parsed)) setSelf(parsed);
            }
            onSet((val) => localStorage.setItem('thumbnailOpacity', String(val)));
        },
    ],
});

// 썸네일 다른 장면 보기(2026-09-14, 어드민 전용) — 켜지면 검색결과 카드 전체가 유튜브
// hqdefault.jpg 대신 카드마다 랜덤한 스토리보드 프레임(1~3.jpg, 대략 25/50/75% 지점)을 보여준다.
// "내가 찾는 게 이건가 싶은데 기본 썸네일(인트로/로딩화면)만으론 확신이 안 서는" 경우를 위한 기능.
export const thumbnailAltModeAtom = atom<boolean>({
    key: 'thumbnailAltModeAtom',
    default: false,
});

// thumbnailAltModeAtom을 켤 때마다 값을 올려서 카드별 랜덤 프레임을 다시 뽑게 만드는 트리거.
export const thumbnailShuffleNonceAtom = atom<number>({
    key: 'thumbnailShuffleNonceAtom',
    default: 0,
});

// 래더 등급 배지(2026-09-15, 어드민 전용) — 켜면 opponentScore가 기록된 강의 썸네일에
// S/A/B/C/D/E/F 등급 배지를 얹어 보여준다(lectureView.ts의 getLadderGrade 참고). 어드민 전용
// 토글이라 공개 UX엔 영향 없어서 기본값 on(사용자 요청, 2026-09-15) — 점수 없는 강의는 배지
// 자체가 안 뜨니 방해도 없음.
// 같은 영상 하나만(2026-10-22, 플로팅 버튼 DistinctVideoButton) — 한 영상의 여러 경기 카드를 하나로. 기본 꺼짐, 페이지를 옮겨도 유지
export const distinctVideoAtom = atom<boolean>({
    key: "distinctVideoAtom",
    default: false,
});

// 빌드·맵 상세의 "최근 영상" 플로팅 토글(2026-10-22) — 선수 페이지처럼 강의 ID 표시·같은 경기 하나만(기본 켬).
// 선수 페이지는 페이지 state로 들지만 허브는 상세 컴포넌트 안쪽 RecentVideos까지 내려야 해서 atom
export const hubShowIdAtom = atom<boolean>({
    key: "hubShowIdAtom",
    default: false,
});
export const hubDedupAtom = atom<boolean>({
    key: "hubDedupAtom",
    default: true,
});

export const ladderGradeModeAtom = atom<boolean>({
    key: 'ladderGradeModeAtom',
    default: true,
});
